import React from "react";
import {
  getColorClasses,
  getTextColorClass,
} from "../../utils/getBackgroundColor";
import { OptimizelyCallToActionButtonsContentArea, TextAndImageBlock as TextAndImageBlockType } from "../../types/optimizely/blocks";
import ArrowRight from "../../assets/arrow-right.svg?react";
import dompurify from "dompurify";
import { renderToString } from "react-dom/server";
import classNames from "classnames";
import { OptimizelyPropertyBoolean } from "../../types/optimizely/base";
import Link from "../Link/Link";
import { getButtonVariant } from "../../utils/getButtonVariant";
import { getHeaderClasses, getHeaderClassesMap } from "../../utils/getHeaderClasses";
import { sanitizeOrPassthrough } from "../../helpers/rteHelpers";
import { SsrTagClassMap } from "../../helpers/rteSsrSanitizer";

interface TextImageBlockProps extends Omit<TextAndImageBlockType, 'created' | 'contentLink' | 'contentType'> {
  className?: string;
  rightClasses?: string;
  rightActions?: React.ReactNode[];
  justifyNormal?: boolean;
  additionalIntro?: string;
  showImageOnRight?: OptimizelyPropertyBoolean;
  showImageAsSecond?: boolean;
  overridesForTextClasses?: string;
  overridesForRightClasses?: string;
  sectionId?: { value: string, propertyDataType: string };
  isHero?: boolean;
  preTitle?: { value: string, propertyDataType: string };
  callToActionButtons?: OptimizelyCallToActionButtonsContentArea;
  showCallToActionButtonsInColumn?: OptimizelyPropertyBoolean;
}

const getAnchorClasses = (backgroundColor: string) =>
  `w-1/2 mt-7 py-3 bg-transparent ${backgroundColor} hover:${backgroundColor} items-center font-bold rounded flex`;

const localPurifier = dompurify();

const TextAndImageBlock = ({
  image,
  preTitle,
  title,
  description,
  className,
  backgroundColor: optiBgColor,
  rightClasses,
  rightActions,
  justifyNormal,
  additionalIntro,
  showImageOnRight,
  showImageAsSecond,
  overridesForTextClasses,
  overridesForRightClasses,
  sectionId,
  isHero,
  callToActionButtons,
  showCallToActionButtonsInColumn,
}: TextImageBlockProps) => {
  const backgroundColor = getColorClasses(optiBgColor?.value);
  const textClass = getTextColorClass(optiBgColor?.value);
  const justifyClass = justifyNormal ? "" : "justify-end";
  const showImageOnRightClass = showImageOnRight?.value ? "md:flex-row-reverse" : "md:flex-row";
  const showImageAsSecondClass = showImageAsSecond ? "" : "flex-col";
  const showCallToActionButtonsInColumnClass = showCallToActionButtonsInColumn?.value ? "flex-col" : "flex-col md:flex-row";

  // Mirrors the "beforeSanitizeAttributes" hook below, for SSR markup to match the client.
  const ssrTagClasses: SsrTagClassMap = {
    a: `${getAnchorClasses(backgroundColor)} font-normal group hover:text-primaryBlue`,
    p: "text-[18px] md:text-[20px]",
    ...getHeaderClassesMap(getHeaderClasses),
  };

  localPurifier.addHook("beforeSanitizeAttributes", (node) => {
    if (node.nodeName && node.nodeName === "A") {
      const hasSvgChild =
        Array.from(node.childNodes).filter((child) => child.nodeName === "SPAN")
          .length > 0;
      node.setAttribute("class", ssrTagClasses.a);

      if (!hasSvgChild) {
        const icon = renderToString(
          <span className="ml-2 group-hover:translate-x-2 transition-all ease-out">
            <ArrowRight />
          </span>
        );
        node.innerHTML += icon;
      }
    }

    if (node.nodeName && node.nodeName === "P") {
      node.setAttribute("class", ssrTagClasses.p);
    }

    if (node.nodeName?.match(/^H[2-6]$/)) {
      node.setAttribute("class", ssrTagClasses[node.nodeName.toLowerCase()]);
    }
  });

  return (
    <div className="flex items-center justify-center" id={sectionId?.value || ''}>
      <div className={classNames("w-full", backgroundColor)}>
        <div className={classNames("flex flex-col-reverse", showImageOnRightClass, showImageAsSecondClass, className)}>
          <div className={classNames("w-full lg:w-1/2", isHero ? "min-h-[640px]" : "")}>
            <img
              src={image?.expandedValue?.url}
              alt={image?.expandedValue?.name}
              className="w-full h-full object-cover "
            />
          </div>
          <div
            className={classNames(
              justifyClass,
              overridesForRightClasses ?? "w-full lg:w-1/2 p-8 md:p-16 flex flex-col whitespace-pre-wrap",
              rightClasses,
              showImageOnRight?.value ? "lg:pl-8" : "lg:pr-8"
            )}
          >
            <div className={!isHero ? 'pr-8' : ''}>
              {additionalIntro ? <p className="md:text-xs text-mob-xs pb-4">{additionalIntro}</p> : <></>}
              {preTitle?.value ? <p className="text-[30px] md:text-[40px]">{preTitle.value}</p> : <></>}
              {title?.value ? <h2
              className={classNames(
                "text-[30px] md:text-[40px] font-bold mb-8",
                textClass
              )}
            >
              {title.value}
            </h2> : <></>}
            <div
              className={overridesForTextClasses ?? "text-gray-600"}
              dangerouslySetInnerHTML={{
                __html: sanitizeOrPassthrough(localPurifier, description.value, { ssrTagClassMap: ssrTagClasses }),
              }}
            />
            {rightActions && (
              <div className="flex mt-10 space-y-4 flex-col lg:flex-row lg:space-x-4 lg:space-y-0 w-1/2 lg:w-full">
                {rightActions}
              </div>
            )}  

            {callToActionButtons?.expandedValue?.length > 0 ? (
              <div className={classNames("flex mt-[30px] gap-4 items-start", showCallToActionButtonsInColumnClass)}>
                {callToActionButtons.expandedValue.map((cta, idx) => (
                  <Link key={idx} text={cta.linkText.value} href={cta.buttonLink.value} variant={getButtonVariant(cta.buttonType.value)} className="max-w-full" />
                ))}
              </div>
            ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextAndImageBlock;
